/**
 * Reference Listed Drug Finder / Reference Product Finder copy — PENDING RA REVIEW.
 *
 * Every user-facing string the drug/biologic finders add lives here, so RA can
 * review it (and the placement decision — on the Existing Devices page or a
 * page of its own — can reword it) in one place. Names follow FDA's own
 * nomenclature: an ANDA references a "Reference Listed Drug" (21 CFR 314.3);
 * a 351(k) biosimilar a "reference product" (PHS Act 351(i)(4)). The internal
 * kinds stay ``generic`` / ``reference``.
 */

export type DrugFinderKind = 'generic' | 'reference';

export function isDrugFinderKind(kind: string | null | undefined): kind is DrugFinderKind {
  return kind === 'generic' || kind === 'reference';
}

/** Short name, e.g. on the run card. */
export const DRUG_FINDER_LABEL: Readonly<Record<DrugFinderKind, string>> = {
  generic: 'Reference Listed Drug Finder (ANDA)',
  reference: 'Reference Product Finder (Biosimilar)',
};

/** The market picker's mode name for the US (the only market). */
export const DRUG_FINDER_US_MODE: Readonly<Record<DrugFinderKind, string>> = {
  generic: 'FDA Reference Listed Drug Finder',
  reference: 'FDA Reference Product Finder',
};

export const DRUG_FINDER_GENERATE_LABEL: Readonly<Record<DrugFinderKind, string>> = {
  generic: 'Find Reference Listed Drug',
  reference: 'Find Reference Product',
};

export const DRUG_FINDER_HERO: Readonly<Record<DrugFinderKind, { title: string; subtitle: string }>> = {
  generic: {
    title: 'Reference Listed Drug Finder',
    subtitle:
      'Identify the Reference Listed Drug for your ANDA submission — grounded in the FDA Orange Book and Drugs@FDA via the REGARA AI agent.',
  },
  reference: {
    title: 'Reference Product Finder',
    subtitle:
      'Identify the originator reference product for your 351(k) biosimilar submission — grounded in the FDA Purple Book and Drugs@FDA via the REGARA AI agent.',
  },
};

/** Result page hero — the "Existing Devices Analysis" counterpart. */
export const DRUG_FINDER_RESULT_HERO: Readonly<Record<DrugFinderKind, { title: string; subtitle: string }>> = {
  generic: {
    title: 'Reference Listed Drug Finder Analysis',
    subtitle: DRUG_FINDER_HERO.generic.subtitle,
  },
  reference: {
    title: 'Reference Product Finder Analysis',
    subtitle: DRUG_FINDER_HERO.reference.subtitle,
  },
};

export const DRUG_FINDER_PICKER_COPY = {
  errorNote:
    'We could not check whether this product’s analysis is up to date. The US market is shown below.',
  selectDescription: 'Pick the market to run the analysis for.',
  currentNote: 'This market already has an analysis built from this product’s current details.',
  previousNote:
    'This market has an earlier analysis you can still open. Select it above to run an updated analysis.',
  currentLinkTitle: "View this market's analysis",
  previousLinkTitle: "View this market's previous analysis",
} as const;

/** Progress steps shown while a run is in flight. */
export const DRUG_FINDER_WORKFLOW_STEPS: Readonly<Record<DrugFinderKind, readonly string[]>> = {
  generic: [
    'Extract product details',
    'Search the Orange Book',
    'Confirm application records in Drugs@FDA',
    'Count AEMS adverse events (last 10 years)',
    'Check drug recalls',
    'Verify citations against FDA records',
    'Generate final analysis',
  ],
  reference: [
    'Extract product details',
    'Search the Purple Book',
    'Confirm application records in Drugs@FDA',
    'List approved biosimilars',
    'Count AEMS adverse events (last 10 years)',
    'Verify citations against FDA records',
    'Generate final analysis',
  ],
};

/** The ANDA / 351(k) dossier form: the chosen RLD / reference product it names
 *  (the device "Primary predicate" card's twin), its finder slot and gate. */
export const DRUG_DOSSIER_COPY: Readonly<
  Record<DrugFinderKind, {
    label: string;
    noun: string;
    change: string;
    analysis: string;
    hint: string;
    anchor: string;
    finderPageNote: string;
    /** The dossier card's line under the pick: what the dossier does with it. */
    cardNote: string;
    /** The finder's last run found nothing — the card says so and links to a re-run. */
    notFound: string;
    /** The pick is one FDA's record could not confirm — the dossier refuses it. */
    unconfirmedPick: string;
    /** …and another candidate IS confirmed (a re-run would be locked): pick that one. */
    unconfirmedChoose: string;
    /** …and none is: the only way forward is a re-run (unlocked in that case). */
    unconfirmedRerun: string;
    rerun: string;
  }>
> = {
  generic: {
    label: 'Reference listed drug',
    noun: 'reference listed drug',
    change: 'Change reference listed drug',
    analysis: 'Reference Listed Drug Analysis',
    hint: 'The ANDA dossier names the reference listed drug you choose in the Reference Listed Drug finder.',
    anchor: 'Run the Reference Listed Drug finder for this product and choose the reference listed drug — the ANDA dossier names it.',
    finderPageNote:
      'After the run, review the candidates and choose your reference listed drug — your ANDA dossier names it as the RLD. Until you choose, the finder’s top match is used by default.',
    cardNote:
      'The ANDA dossier will name this as its Reference Listed Drug and build the pharmaceutical-equivalence and bioequivalence sections against it. Keep it, or change it on the finder page.',
    notFound:
      'The Reference Listed Drug finder found no reference listed drug for this product. The ANDA dossier needs one — run the finder again.',
    rerun: 'Re-run the Reference Listed Drug finder',
    unconfirmedPick:
      'FDA could not confirm this reference listed drug, so the ANDA dossier can’t be generated from it.',
    unconfirmedChoose: 'Choose a confirmed one on the finder result page.',
    unconfirmedRerun: 'None of this run’s candidates is confirmed — re-run the finder.',
  },
  reference: {
    label: 'Reference product',
    noun: 'reference product',
    change: 'Change reference product',
    analysis: 'Reference Product Analysis',
    hint: 'The 351(k) dossier names the reference product you choose in the Reference Product finder.',
    anchor: 'Run the Reference Product finder for this product and choose the reference product — the 351(k) dossier names it.',
    finderPageNote:
      'After the run, review the candidates and choose your reference product — your 351(k) dossier names it as the reference product. Until you choose, the finder’s top match is used by default.',
    cardNote:
      'The 351(k) dossier will name this as its reference product and build the biosimilarity sections against it. Keep it, or change it on the finder page.',
    notFound:
      'The Reference Product finder found no reference product for this product. The 351(k) dossier needs one — run the finder again.',
    rerun: 'Re-run the Reference Product finder',
    unconfirmedPick:
      'FDA could not confirm this reference product, so the 351(k) dossier can’t be generated from it.',
    unconfirmedChoose: 'Choose a confirmed one on the finder result page.',
    unconfirmedRerun: 'None of this run’s candidates is confirmed — re-run the finder.',
  },
};

/** Which drug finder a dossier pathway reads (backend app/dossier/usa/config). */
export const DOSSIER_DRUG_FINDER: Readonly<Record<string, DrugFinderKind>> = {
  anda: 'generic',
  biosimilar: 'reference',
};

/** Result-view section headings and empty states. */
export const DRUG_RESULT_COPY = {
  interchangeable: 'Interchangeable',
  rld: 'RLD',
  rs: 'Reference Standard',
  // Shown instead of "Verified · Drugs@FDA" when FDA's record could not confirm
  // the ingredient (QA audit fb5b02ac).
  unconfirmed: 'Match not confirmed by FDA',
  provenanceHeading: {
    generic: 'Reference Listed Drug Analysis',
    reference: 'Reference Product Analysis',
  },
  // The "a newer … exists" / "product has changed" banner's name for it.
  staleAnalysisName: {
    generic: 'Reference Listed Drug analysis',
    reference: 'Reference Product analysis',
  },
  // Under the tiles, like the device finder's legend: what the grade is based on
  // (each agent prompt's high / medium / low rule).
  bandLegend: {
    generic:
      'Match confidence based on active ingredient, dosage form, strength and route against the Orange Book, with the brand and sponsor confirmed in Drugs@FDA.',
    reference:
      'Match confidence based on active substance, indication overlap, sponsor and approval date against the Purple Book, confirmed in Drugs@FDA.',
  },
  // Same labels as the device finder's tiles.
  tiles: {
    candidates: 'Total Candidates',
    high: 'High Confidence',
    medium: 'Medium Confidence',
    low: 'Low Confidence',
  },
  candidatesHeading: {
    generic: 'Reference Listed Drug (RLD) Candidates',
    reference: 'Reference Product Candidates (351(k))',
  },
  rationale: 'Match rationale',
  productSummary: 'Product Summary',
  // Adverse events — the drug-side MAUDE row on each candidate card. FDA replaced
  // FAERS with AEMS on 2026-03-11; drugs and biologics say AEMS only (user, 2026-10-05).
  faersRow: {
    label: 'AEMS',
    fullName: 'FDA Adverse Event Monitoring System (AEMS)',
    notAssessed: 'not assessed',
    reports: (n: number) => `${n.toLocaleString('en-US')} report${n === 1 ? '' : 's'}`,
    basis: (name: string | null) =>
      name ? `reports naming ${name}, last 10 years` : 'this product, last 10 years',
    // The device MAUDE row's " · N for the whole category (…), context only", for
    // every maker's reports of the ingredient. Never decides the warning.
    context: (n: number, ingredient: string) =>
      ` · ${n.toLocaleString('en-US')} for ${ingredient} across all makers, context only`,
    // FDA's public dashboard. AEMS has no per-report web pages to link to (MAUDE
    // does), and the dashboard cannot be opened pre-filtered to a product.
    dashboard: 'Search the AEMS dashboard',
    dashboardUrl:
      'https://fis.fda.gov/sense/app/95239e26-e0be-42d9-a960-9a5f7f1c25ee/sheet/7a47a261-d58b-4203-a8aa-6d3021737452/state/analysis',
    show: (n: number) => `show ${n} report${n === 1 ? '' : 's'}`,
    hide: 'hide reports',
    capped: (shown: number, total: number) =>
      `Showing the ${shown} most recent of ${total.toLocaleString('en-US')} reports.`,
    columns: ['Report', 'Received', 'Serious', 'Outcome', 'Reactions'] as const,
    source:
      'Source: FDA Adverse Event Monitoring System (AEMS) via openFDA (drug/event) — reports naming this product, last 10 years. A report does not show that the product caused the event. FDA publishes no per-report pages, so report IDs are not linked.',
    yes: 'Yes',
    no: 'No',
  },
  // The badge row under each candidate's name — the device card's recalls +
  // MAUDE badges, with AEMS and "this product" for drugs and biologics.
  safetyBadges: {
    recalls: (n: number) => `${n.toLocaleString('en-US')} recall${n === 1 ? '' : 's'}`,
    noRecalls: 'No recalls',
    recallsNotChecked: 'Recalls not checked',
    aems: (n: number) =>
      `${n.toLocaleString('en-US')} AEMS report${n === 1 ? '' : 's'} · this product`,
    aemsNone: 'AEMS · none for this product',
    aemsNotChecked: 'AEMS · not checked for this product',
  },
  // The device card's "Substantial Equivalence" (a 510(k) term) for drugs.
  equivalenceTitle: {
    generic: 'Pharmaceutical Equivalence',
    reference: 'Biosimilarity',
  },
  gapsTitle: 'Gaps to Address',
  defaultBanner: 'Finder’s default — not yet confirmed by you',
  warnHintDefault: 'Review it, then keep it or pick a different one below.',
  pick: {
    generic: {
      noun: 'reference listed drug',
      useLabel: 'Use as primary reference listed drug',
      defaultTitle: 'The finder picked a default reference listed drug for you',
      note: 'Choosing the reference listed drug is your regulatory decision — keep this one or pick a different candidate below.',
      selectedBanner: 'Reference listed drug — your selection',
      selectedLine: 'Reference listed drug:',
      disabledTitle: 'Only admins can set the reference listed drug',
      warnDefaultLead: 'No reference listed drug selected, so the finder’s default is',
      warnHeadline: (h: string) => `Safety history: this reference listed drug has ${h}.`,
      warnHintPick: 'Review it on the card below before relying on it for your ANDA.',
      warnQuestion: 'Do you still want to pick it as your primary reference listed drug?',
    },
    reference: {
      noun: 'reference product',
      useLabel: 'Use as primary reference product',
      defaultTitle: 'The finder picked a default reference product for you',
      note: 'Choosing the reference product is your regulatory decision — keep this one or pick a different candidate below.',
      selectedBanner: 'Reference product — your selection',
      selectedLine: 'Reference product:',
      disabledTitle: 'Only admins can set the reference product',
      warnDefaultLead: 'No reference product selected, so the finder’s default is',
      warnHeadline: (h: string) => `Safety history: this reference product has ${h}.`,
      warnHintPick: 'Review it on the card below before relying on it for your 351(k) application.',
      warnQuestion: 'Do you still want to pick it as your primary reference product?',
    },
  },
  provenanceNote:
    'Identities (application number, sponsor, brand name) are verified against FDA’s Drugs@FDA database. The match and its confidence are recommendations — confirm with a qualified regulatory professional before relying on them.',
  limitationsIntro:
    'The analysis could not resolve these points from the product’s data or uploaded specification document. Add the missing details to the specification (re-upload it) and re-run for a stronger, more accurate match:',
  fields: {
    activeIngredient: 'Active ingredient',
    properName: 'Proper name',
    dosageForm: 'Dosage form',
    strength: 'Strength',
    route: 'Route',
    dose: 'Dose',
    approvalDate: 'Approval date',
    teCode: 'Therapeutic equivalence code',
    indications: 'Indications',
    exclusivityExpires: 'Exclusivity expires',
    exclusivityNotChecked: 'Not checked — Purple Book unavailable',
    fdaMarketingStatus: 'FDA marketing status',
    fdaStatusNote: 'FDA note',
  },
  generic: {
    none: 'No Reference Listed Drug could be identified from FDA records.',
  },
  reference: {
    alternates: 'Approved Biosimilars of this Reference Product',
    // Why these carry no Biosimilarity / Gaps lists and can't be picked: the
    // reference product must be a 351(a)-licensed product (PHS Act 351(i)(4)).
    alternatesNote:
      'Biosimilars FDA has already approved against this reference product. A biosimilar cannot serve as your reference product (PHS Act 351(i)(4)).',
    // The list is read from the FDA Purple Book after the run, never written by the AI.
    purpleBookNone: (release: string | null) =>
      `The FDA Purple Book${release ? ` (${release})` : ''} lists no licensed biosimilars of this reference product.`,
    purpleBookUnavailable:
      'Couldn’t load the FDA’s list of approved biosimilars (Purple Book) for this run. Re-run the finder later to see it.',
    none: 'No reference product could be identified from FDA records.',
  },
  parseError: 'This analysis could not be displayed. Download the PDF for the full report.',
} as const;
